import {
  AuditOutlined,
  CloseCircleOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  FormOutlined,
  PlusOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import LockResetIcon from '@mui/icons-material/LockReset'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Menu,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AlertDialog from 'components/AlertDialog'
import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import ButtonActionBar from 'components/ButtonActionBar'
import ConfirmDialog from 'components/ConfirmDialog'
import EditUserDialog from 'components/EditUserDialog'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import NoticeDialog from 'components/NoticeDialog'
import SearchInput from 'components/SearchInput'
import SimpleTitle from 'components/SimpleTitle'
import useAuth from 'hooks/useAuth'
import useLocalStorage from 'hooks/useLocalStorage'
import { useSearchUsersWithPlanInfo } from 'hooks/useUsers'
import type { JSX } from 'react'
import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Permissions } from 'types/permissions'
import type { KeyedObject } from 'types/root'
import type { UserQueryParams, UserWithPlanInfo } from 'types/user'
import { getOrganizationIdsWithPermissions, getOrganizationLabelMap } from 'types/user-profile'
import { BulkUserActionRequest, BulkUserActionResponse } from 'types/user'
import { useBulkSendUserInvite, useBulkRequireUserResetPassword } from 'hooks/useUsers'
import { useGlobalState } from 'contexts/GlobalStateContext'

const Organization: React.FC = () => {
  const { token, profile, refetchProfile } = useAuth()
  const { checkSubscriptionTierFeature } = useGlobalState()
  const location = useLocation()
  const [search, setSearch] = useState('')
  const theme = useTheme()
  const [page, setPage] = useLocalStorage<number>('team-page', 0, profile?.user?.id)
  const [rowsPerPage, setRowsPerPage] = useLocalStorage<number>('team-rows-per-page', 100, profile?.user?.id)
  const [selectedOrganizations, setSelectedOrganizations] = useLocalStorage<string[]>(
    'team-selected-organizations',
    [],
    profile?.user?.id
  )
  const [includeWithoutPlans, setIncludeWithoutPlans] = useLocalStorage<boolean>(
    'team-include-without-plans',
    true,
    profile?.user?.id
  )
  const [includeUnapproved, setIncludeUnapproved] = useLocalStorage<boolean>(
    'team-include-unapproved',
    true,
    profile?.user?.id
  )
  const [includeApproved, setIncludeApproved] = useLocalStorage<boolean>(
    'team-include-approved',
    true,
    profile?.user?.id
  )
  const [includeCurrentMembers, setIncludeCurrentMembers] = useLocalStorage<boolean>(
    'team-include-current-members',
    true,
    profile?.user?.id
  )
  const [includeFormerMembers, setIncludeFormerMembers] = useLocalStorage<boolean>(
    'team-include-former-members',
    false,
    profile?.user?.id
  )
  const [sort, setSort] = useLocalStorage<string>('team-sort', 'lastName', profile?.user?.id)
  const [order, setOrder] = useLocalStorage<string>('team-order', 'asc', profile?.user?.id)
  const tableContainerRef = useRef(null)

  // const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [createMode, setCreateMode] = useState(false)
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false)
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [showActionBar, setShowActionBar] = useState(false)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [inviteResults, setInviteResults] = useState<BulkUserActionResponse | null>(null)
  const [showInviteResults, setShowInviteResults] = useState(false)
  const [showConfirmInvite, setShowConfirmInvite] = useState(false)
  const [resetPasswordResults, setResetPasswordResults] = useState<BulkUserActionResponse | null>(null)
  const [showResetPasswordResults, setShowResetPasswordResults] = useState(false)
  const [showConfirmReset, setShowConfirmReset] = useState(false)
  const [showResetPasswordErrorDialog, setShowResetPasswordErrorDialog] = useState(false)
  const [showInviteErrorDialog, setShowInviteErrorDialog] = useState(false)
  const pendingUserIds = useRef<string[]>([])
  const [organizationMap, setOrganizationMap] = useState<Record<string, string>>({})
  const [moreFiltersAnchorEl, setMoreFiltersAnchorEl] = useState<null | HTMLElement>(null)
  const moreFiltersOpen = Boolean(moreFiltersAnchorEl)

  const queryParams: UserQueryParams = {
    organizationIds: selectedOrganizations,
    searchTerm: search,
    pagination: {
      page: page + 1,
      perPage: rowsPerPage,
    },
    sort: [{ field: sort, order }],
    includeWithoutPlans,
    includeUnapproved,
    includeApproved,
    includeCurrentMembers,
    includeFormerMembers,
  }

  const {
    users = [],
    isLoading,
    pagination = { totalItems: 0, totalPages: 1 },
    mutate: mutateUsers,
  } = useSearchUsersWithPlanInfo(token!, queryParams)

  const { bulkSendUserInvite, isSending: isBulkSending, error: bulkSendError } = useBulkSendUserInvite(token!)
  const {
    bulkUserResetPassword,
    isSending: isBulkResetting,
    error: bulkResetError,
  } = useBulkRequireUserResetPassword(token!)

  const organizationIdsWithMembersWrite =
    profile != null ? getOrganizationIdsWithPermissions(profile, [Permissions.MEMBERS_WRITE]) : []
  const organizationIdsWithPlansRead =
    profile != null ? getOrganizationIdsWithPermissions(profile, [Permissions.PLANS_READ]) : []
  const organizationIdsWithPlanWrite =
    profile != null ? getOrganizationIdsWithPermissions(profile, [Permissions.PLANS_WRITE]) : []

  const canManageTeamStructure = organizationIdsWithMembersWrite.length > 0
  const canManagePlans = organizationIdsWithPlansRead.length > 0

  useEffect(() => {
    if (profile !== null) {
      setOrganizationMap(getOrganizationLabelMap(profile!))
    } else {
      setOrganizationMap({})
    }
  }, [profile])

  useEffect(() => {
    if (tableContainerRef.current) {
      ;(tableContainerRef.current as HTMLElement).scrollTop = 0
    }
  }, [sort, order, page, rowsPerPage])

  // Handle bulk operation errors
  useEffect(() => {
    if (bulkSendError) {
      setErrorMessage('Failed to send user invitations. Please try again.')
      setShowErrorDialog(true)
    }
  }, [bulkSendError])

  useEffect(() => {
    if (bulkResetError) {
      setErrorMessage('Failed to reset user passwords. Please try again.')
      setShowErrorDialog(true)
    }
  }, [bulkResetError])

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
    setRowsPerPage(+event?.target?.value!)
    setPage(0)
  }

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
  }, [])

  // Handle sorting
  const handleSort = (property: string) => {
    const isAscending = sort === property && order === 'asc'
    setOrder(isAscending ? 'desc' : 'asc')
    setSort(property)
  }

  // Handle user selection toggle
  const handleUserToggle = (userId: string) => {
    setSelectedUserIds((prev) => {
      const newSelection = prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
      setShowActionBar(newSelection.length > 0)
      return newSelection
    })
  }

  // Handle select all / deselect all
  const handleSelectAll = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([])
      setShowActionBar(false)
    } else {
      setSelectedUserIds(users.map((user) => user.id))
      setShowActionBar(true)
    }
  }

  // Handle bulk actions - show confirmation dialogs
  const handleBulkSendUserInvite = () => {
    if (!selectedUserIds.length) return
    pendingUserIds.current = [...selectedUserIds]
    setShowConfirmInvite(true)
  }

  const handleRefreshData = async () => {
    refetchProfile()
    mutateUsers()
  }

  // Confirm bulk invite
  const handleConfirmBulkInvite = async () => {
    console.log('Bulk send user invites:', pendingUserIds.current)
    const request: BulkUserActionRequest = { userIds: [...pendingUserIds.current] }
    try {
      const response = await bulkSendUserInvite(request)
      console.log('Bulk send user invites response:', response)
      setInviteResults(response.data)
      setShowConfirmInvite(false)
      setShowInviteResults(true)
      setSelectedUserIds([])
      setShowActionBar(false)
      pendingUserIds.current = []
    } catch (error) {
      console.error('Error sending user invites:', error)
    }
  }

  const checkInviteResults = (): boolean => {
    if (inviteResults) {
      for (const result of inviteResults.results) {
        if (result.status !== 200) {
          return false
        }
      }
    }
    return true
  }

  const handleInviteResultsClose = () => {
    setShowInviteResults(false)
    pendingUserIds.current = []
  }

  const handleShowInviteError = () => {
    setShowInviteResults(false)
    setShowInviteErrorDialog(true)
  }

  const getInviteErrorDetails = (): string => {
    if (!inviteResults) return 'Unknown error occurred.'

    const errors = inviteResults.results
      .filter((result) => result.status !== 200)
      .map((result) => {
        const user = users.find((u) => u.id === result.userId)
        const userName = user ? user.firstName + ' ' + user.lastName : `User ID ${result.userId}`
        return `${userName}: ${result.error || `Error ${result.status}`}`
      })

    return errors.length > 0 ? errors.join('\n') : 'Unknown error occurred.'
  }

  const handleCancelBulkInvite = () => {
    setShowConfirmInvite(false)
    pendingUserIds.current = []
  }

  // Confirm bulk reset password
  const handleConfirmBulkReset = async () => {
    console.log('Bulk reset user passwords:', pendingUserIds.current)
    const request: BulkUserActionRequest = { userIds: [...pendingUserIds.current] }
    try {
      const response = await bulkUserResetPassword(request)
      console.log('Bulk reset user passwords response:', response)
      setResetPasswordResults(response.data)
      setShowConfirmReset(false)
      setShowResetPasswordResults(true)
      setSelectedUserIds([])
      setShowActionBar(false)
      pendingUserIds.current = []
    } catch (error) {
      console.error('Error resetting user passwords:', error)
    }
  }

  const checkResetPasswordResults = (): boolean => {
    if (resetPasswordResults) {
      for (const result of resetPasswordResults.results) {
        if (result.status !== 200) {
          return false
        }
      }
    }
    return true
  }
  const handleBulkUserResetPassword = () => {
    if (!selectedUserIds.length) return
    pendingUserIds.current = [...selectedUserIds]
    setShowConfirmReset(true)
  }

  const handleResetPasswordResultsClose = () => {
    setShowResetPasswordResults(false)
    pendingUserIds.current = []
  }

  const handleCancelBulkReset = () => {
    setShowConfirmReset(false)
    pendingUserIds.current = []
  }

  const handleShowResetPasswordError = () => {
    setShowResetPasswordResults(false)
    setShowResetPasswordErrorDialog(true)
  }

  const getResetPasswordErrorDetails = (): string => {
    if (!resetPasswordResults) return 'Unknown error occurred.'

    const errors = resetPasswordResults.results
      .filter((result) => result.status !== 200)
      .map((result) => {
        const user = users.find((u) => u.id === result.userId)
        const userName = user ? user.firstName : `User ID ${result.userId}`
        return `${userName}: ${result.error || `Error ${result.status}`}`
      })

    return errors.length > 0 ? errors.join('\n') : 'Unknown error occurred.'
  }

  const handleCancelSelection = () => {
    setSelectedUserIds([])
    setShowActionBar(false)
  }

  const handleCloseErrorDialog = () => {
    setShowErrorDialog(false)
    setErrorMessage('')
  }

  // table columns
  interface ColumnProps {
    id: string
    label: string
    sx?: any
    align?: 'right' | 'left' | 'inherit' | 'center' | 'justify' | undefined
    format?: (value: any, row: any) => string | boolean | JSX.Element
  }

  const navigate = useNavigate()

  const columns: ColumnProps[] = [
    {
      id: 'select',
      label: '',
      sx: {
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        position: 'sticky !important',
      },
      format: (value: any, row: UserWithPlanInfo) => (
        <Checkbox checked={selectedUserIds.includes(row.id)} onChange={() => handleUserToggle(row.id)} />
      ),
    },
    {
      id: 'firstName',
      label: 'First Name',
      sx: {
        minWidth: 100,
        maxWidth: 150,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        position: 'sticky !important',
      },
    },
    {
      id: 'lastName',
      label: 'Last Name',
      sx: {
        minWidth: 100,
        maxWidth: 150,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        position: 'sticky !important',
      },
      format: (value: any, row: any) => row.lastName,
    },
    {
      id: 'organizationName',
      label: 'Team',
      sx: {
        minWidth: 170,
        maxWidth: 250,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        position: 'sticky !important',
      },
      format: (value: any, row: UserWithPlanInfo) => row.organizationName,
    },
    {
      id: 'position',
      label: 'Position',
      sx: {
        minWidth: 170,
        maxWidth: 250,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        position: 'sticky !important',
      },
    },
    {
      id: 'email',
      label: 'Email',
      align: 'left',
      sx: {
        minWidth: 170,
        maxWidth: 250,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        position: 'sticky !important',
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      sx: {
        minWidth: 100,
        maxWidth: 100,
        position: 'sticky !important',
      },
      format: (userId: string, row: UserWithPlanInfo) => {
        // const hasApprovedPlan = row.planApproved
        const hasUnapprovedPlan = row.approvalBatchId != null && !row.planApproved
        const hasNoPlan = !row.approvalBatchId

        return (
          <Box display="flex">
            {organizationIdsWithPlansRead.includes(row.organizationId) &&
              checkSubscriptionTierFeature('INCENTIVE.ALL') && (
                <Tooltip title="View Employee Compensation Plan">
                  <IconButton color="primary" onClick={() => navigate(`/employee/${row.id}/compplan`)}>
                    <EyeOutlined />
                  </IconButton>
                </Tooltip>
              )}
            {hasUnapprovedPlan && checkSubscriptionTierFeature('INCENTIVE.ALL') && (
              <Tooltip title="Review Unapproved Plan">
                <IconButton color="primary" onClick={() => navigate(`/admin/approvals/${row.approvalBatchId}/edit`)}>
                  <AuditOutlined />
                </IconButton>
              </Tooltip>
            )}
            {hasNoPlan &&
              organizationIdsWithPlanWrite.includes(row.organizationId) &&
              checkSubscriptionTierFeature('INCENTIVE.ALL') && (
                <Tooltip title="Create New Plan">
                  <IconButton
                    color="primary"
                    onClick={() =>
                      navigate(`/admin/templates/select/${row.id}`, {
                        state: { origin: location.pathname },
                      })
                    }
                  >
                    <FormOutlined />
                  </IconButton>
                </Tooltip>
              )}
            {!hasNoPlan &&
              organizationIdsWithPlanWrite.includes(row.organizationId) &&
              checkSubscriptionTierFeature('INCENTIVE.ALL') && (
                <Tooltip title="Edit Employee Compensation Plan">
                  <IconButton
                    color="primary"
                    onClick={() =>
                      navigate(`/create-comp-plan?userId=${row.id}`, {
                        state: { from: '/organization' },
                      })
                    }
                  >
                    <EditOutlined />
                  </IconButton>
                </Tooltip>
              )}
            {organizationIdsWithMembersWrite.includes(row.organizationId) && (
              <>
                <Tooltip title="Edit Employee Profile">
                  <IconButton
                    color="primary"
                    onClick={() => {
                      console.log('Setting selected user id', row.id)
                      setSelectedUserId(row.id)
                      setCreateMode(false)
                      setEditUserDialogOpen(true)
                      console.log(editUserDialogOpen)
                    }}
                  >
                    <UserOutlined />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        )
      },
    },
  ]

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 4 }}>
          <SimpleTitle title="Team" />
        </Grid>
        <Grid size={{ xs: 6 }}></Grid>
        {canManageTeamStructure && (
          <Grid size={{ xs: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<TeamOutlined />}
              onClick={() => navigate('/admin/organizations/manage')}
              sx={{ borderColor: 'transparent', whiteSpace: 'nowrap', width: '100%' }}
            >
              Manage Team Structure
            </Button>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="team-search-input"
            key="team-search-input"
            onSearch={handleSearch}
            initialValue={search}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 9 }}>
          <Box display="flex" gap={2} justifyContent="flex-end" alignItems="center">
            <MultiOrganizationSelector
              userProfile={profile!}
              selectedOrganizationIds={selectedOrganizations}
              handleChange={setSelectedOrganizations}
              labelBackgroundColor={theme.palette.background.default}
              width="300px"
            />
            <Tooltip title="More Filters">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FilterOutlined />}
                onClick={(e) => setMoreFiltersAnchorEl(e.currentTarget)}
                sx={{ height: '40px', minWidth: '140px' }}
              >
                More Filters
              </Button>
            </Tooltip>
            <Menu
              anchorEl={moreFiltersAnchorEl}
              open={moreFiltersOpen}
              onClose={() => setMoreFiltersAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: { minWidth: '280px', mt: 1 },
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
                  Member Status
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeCurrentMembers}
                        onChange={(e) => setIncludeCurrentMembers(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show Current Members"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeFormerMembers}
                        onChange={(e) => setIncludeFormerMembers(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show Former Members"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Stack>
              </Box>
              {canManagePlans && [
                <Divider key="plan-filters-divider" sx={{ my: 1 }} />,
                <Box key="plan-filters" sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Plan Filters
                  </Typography>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeWithoutPlans}
                          onChange={(e) => setIncludeWithoutPlans(e.target.checked)}
                          size="small"
                        />
                      }
                      label="No Plan"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeUnapproved}
                          onChange={(e) => setIncludeUnapproved(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Plan Not Approved"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeApproved}
                          onChange={(e) => setIncludeApproved(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Plan Approved"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  </Stack>
                </Box>,
              ]}
            </Menu>
            <Tooltip title="Clear Filters">
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectedOrganizations([])
                  setIncludeWithoutPlans(true)
                  setIncludeUnapproved(true)
                  setIncludeApproved(true)
                  setIncludeCurrentMembers(true)
                  setIncludeFormerMembers(false)
                  setSearch('')
                }}
              >
                <CloseCircleOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      {isLoading ? (
        <Grid container spacing={12}>
          <Grid
            size={{ xs: 12 }}
            style={{
              display: 'flex', // Enables flexbox
              justifyContent: 'center', // Centers horizontally
              alignItems: 'center', // Centers vertically
              height: '100%', // Takes full height of the container
            }}
          >
            <CircularProgress color="primary" />
            Loading
          </Grid>
        </Grid>
      ) : (
        <Paper>
          {/* table */}
          <TableContainer ref={tableContainerRef} sx={{ maxHeight: 'calc(100vh - 400px)', minHeight: '300px' }}>
            <Table
              stickyHeader
              size="small"
              aria-label="team-table"
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                '& .MuiTableCell-stickyHeader': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                },
              }}
            >
              <TableHead
                sx={{
                  '& th': {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider} !important`,
                  },
                }}
              >
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      id={`cell-col-${column.id || ''}`}
                      sx={{
                        ...column.sx,
                      }}
                      key={column.id}
                      align={column.align}
                    >
                      {column.id === 'select' ? (
                        <Checkbox
                          checked={users.length > 0 && selectedUserIds.length === users.length}
                          indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < users.length}
                          onChange={handleSelectAll}
                        />
                      ) : column.id === 'actions' ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sort === column.id}
                          direction={sort === column.id ? (order as 'asc' | 'desc') : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length > 0 ? (
                  users.map((row: KeyedObject) => (
                    <TableRow
                      id={`table-row-${row.id}`}
                      sx={{ py: 3 }}
                      role="checkbox"
                      tabIndex={-1}
                      key={`table-row-${row.id}`}
                    >
                      {columns.map((column) => {
                        let value
                        if (column.id === 'name') {
                          value = `${row.firstName} ${row.lastName}`
                        } else if (column.id === 'organization') {
                          const orgId = row.metadata?.organizationId
                          value = orgId ? organizationMap[orgId] || '' : ''
                        } else {
                          value = row[column.id]
                        }
                        return (
                          <TableCell
                            id={`cell-${column.id}`}
                            key={`cell-${column.id}`}
                            align={column.align}
                            sx={{
                              ...column.sx,
                            }}
                          >
                            {column.format ? column.format(value, row) : value}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" sx={{ px: '20px' }}>
            {canManageTeamStructure ? (
              <>
                <AnimateButton>
                  <Tooltip title="Add a new team member">
                    <Button
                      variant="contained"
                      startIcon={<PlusOutlined />}
                      color="primary"
                      onClick={() => {
                        setSelectedUserId(null)
                        setCreateMode(true)
                        setEditUserDialogOpen(true)
                      }}
                    >
                      Team Member
                    </Button>
                  </Tooltip>
                </AnimateButton>
              </>
            ) : (
              <div />
            )}
            {/* table pagination */}
            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component="div"
              count={pagination.totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
          {/* table pagination */}
        </Paper>
      )}

      <EditUserDialog
        open={editUserDialogOpen}
        onClose={() => {
          // Force refresh of user data to ensure table shows latest changes
          handleRefreshData()
          setEditUserDialogOpen(false)
        }}
        userId={selectedUserId}
        createMode={createMode}
      />

      <ButtonActionBar show={showActionBar} maxWidth="800px" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
            {selectedUserIds.length} {selectedUserIds.length === 1 ? 'user' : 'users'} selected
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CloseCircleOutlined />}
            onClick={handleCancelSelection}
          >
            Cancel
          </Button>
          <Button startIcon={<MailOutlineIcon />} onClick={handleBulkSendUserInvite}>
            Bulk Invite
          </Button>
          <Button startIcon={<LockResetIcon />} onClick={handleBulkUserResetPassword}>
            Bulk Reset Password
          </Button>
        </Box>
      </ButtonActionBar>

      <NoticeDialog
        show={isBulkSending || isBulkResetting}
        title={isBulkSending ? 'Sending Invites...' : 'Resetting Passwords...'}
        message={
          isBulkSending
            ? 'Please wait while we send invitations to the selected users.'
            : 'Please wait while we reset passwords for the selected users.'
        }
        showSpinner
      />

      <AlertDialog
        show={showErrorDialog}
        onClose={handleCloseErrorDialog}
        title="Error"
        message={errorMessage}
        okText="OK"
        okColor="error"
      />

      <ConfirmDialog
        show={showConfirmInvite}
        onConfirm={handleConfirmBulkInvite}
        onCancel={handleCancelBulkInvite}
        title="Confirm Bulk Invite"
        message={`Are you sure you want to send invitations to ${pendingUserIds.current.length} ${pendingUserIds.current.length === 1 ? 'user' : 'users'}?`}
        confirmText="Send Invites"
        cancelText="Cancel"
        confirmColor="primary"
      />

      <ConfirmDialog
        show={showConfirmReset}
        onConfirm={handleConfirmBulkReset}
        onCancel={handleCancelBulkReset}
        title="Confirm Bulk Password Reset"
        message={`Are you sure you want to reset passwords for ${pendingUserIds.current.length} ${pendingUserIds.current.length === 1 ? 'user' : 'users'}? This will require them to reset their password on next login.`}
        confirmText="Reset Passwords"
        cancelText="Cancel"
        confirmColor="warning"
      />

      {showResetPasswordResults &&
        (checkResetPasswordResults() ? (
          <Snackbar
            open={showResetPasswordResults}
            autoHideDuration={3000}
            onClose={handleResetPasswordResultsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert onClose={handleResetPasswordResultsClose} severity="success" sx={{ width: '100%' }}>
              Reset Passwords Request Successful
            </Alert>
          </Snackbar>
        ) : (
          <Snackbar
            open={showResetPasswordResults}
            autoHideDuration={6000}
            onClose={handleResetPasswordResultsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert
              onClose={handleResetPasswordResultsClose}
              severity="error"
              sx={{ width: '100%' }}
              action={
                <Button color="inherit" size="small" onClick={handleShowResetPasswordError}>
                  VIEW DETAILS
                </Button>
              }
            >
              Reset Passwords Request Failed
            </Alert>
          </Snackbar>
        ))}

      <AlertDialog
        show={showResetPasswordErrorDialog}
        onClose={() => setShowResetPasswordErrorDialog(false)}
        title="Reset Password Errors"
        message={getResetPasswordErrorDetails()}
        okText="Close"
        okColor="primary"
      />

      {showInviteResults &&
        (checkInviteResults() ? (
          <Snackbar
            open={showInviteResults}
            autoHideDuration={3000}
            onClose={handleInviteResultsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert onClose={handleInviteResultsClose} severity="success" sx={{ width: '100%' }}>
              Invitations Sent Successfully
            </Alert>
          </Snackbar>
        ) : (
          <Snackbar
            open={showInviteResults}
            autoHideDuration={6000}
            onClose={handleInviteResultsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert
              onClose={handleInviteResultsClose}
              severity="error"
              sx={{ width: '100%' }}
              action={
                <Button color="inherit" size="small" onClick={handleShowInviteError}>
                  VIEW DETAILS
                </Button>
              }
            >
              Invitations Sent Failed
            </Alert>
          </Snackbar>
        ))}

      <AlertDialog
        show={showInviteErrorDialog}
        onClose={() => setShowInviteErrorDialog(false)}
        title="Invitation Errors"
        message={getInviteErrorDetails()}
        okText="Close"
        okColor="primary"
      />
    </>
  )
}

export default Organization
